import {FC, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  ContentLifecycleStrip,
  getDocsByContentId,
  getRawContentDoc,
  getRenderJobsForContent,
} from '../../../print-story'
import {Content} from '../core/_models'

type Props = {
  content: Content
  enabled?: boolean
}

const BASIC_LIFECYCLE_REFETCH_INTERVAL = 5_000

const getLiveLifecycleQueryOptions = (enabled: boolean) => ({
  cacheTime: 0,
  enabled,
  refetchInterval: enabled ? BASIC_LIFECYCLE_REFETCH_INTERVAL : false,
  refetchIntervalInBackground: true,
  refetchOnReconnect: true,
  refetchOnWindowFocus: true,
  staleTime: 0,
} as const)

export const ContentBasicLifecycle: FC<Props> = ({content, enabled = true}) => {
  const contentId = content.id || ''
  const liveQueryEnabled = Boolean(contentId) && enabled

  const rawContentQuery = useQuery(
    ['content-basic-lifecycle', 'content', contentId],
    () => getRawContentDoc(contentId),
    getLiveLifecycleQueryOptions(liveQueryEnabled)
  )

  const codesQuery = useQuery(
    ['content-basic-lifecycle', 'codes', contentId],
    () => getDocsByContentId('codes', contentId, 'codeId'),
    getLiveLifecycleQueryOptions(liveQueryEnabled)
  )

  const codeIds = useMemo(
    () => (codesQuery.data || []).map((code) => String(code.codeId || code.id)).filter(Boolean),
    [codesQuery.data]
  )

  const renderJobsQuery = useQuery(
    ['content-basic-lifecycle', 'renderJobs', contentId, codeIds.join('|')],
    () => getRenderJobsForContent(contentId, codeIds),
    getLiveLifecycleQueryOptions(liveQueryEnabled)
  )

  const printJobsQuery = useQuery(
    ['content-basic-lifecycle', 'printJobs', contentId],
    () => getDocsByContentId('printJobs', contentId, 'jobId'),
    getLiveLifecycleQueryOptions(liveQueryEnabled)
  )

  if (rawContentQuery.isLoading || codesQuery.isLoading || printJobsQuery.isLoading) {
    return (
      <div className='border border-dashed rounded p-4 mb-7 text-muted fw-semibold'>
        Loading lifecycle...
      </div>
    )
  }

  return (
    <ContentLifecycleStrip
      content={rawContentQuery.data || (content as any)}
      codes={codesQuery.data || []}
      renderJobs={renderJobsQuery.data || []}
      printJobs={printJobsQuery.data || []}
    />
  )
}
